import React, { useState } from 'react';
import axios from 'axios';
import { useSite, IMG } from '../components/StoreLayout';
import StorePhoneInput from '../components/StorePhoneInput';
import Captcha from '../components/Captcha';
import useSEO from '../useSEO';
import { trackEvent } from '../analytics';

function parseJson(v, fallback) {
  try { const p = JSON.parse(v || ''); return Array.isArray(p) ? p : fallback; } catch { return fallback; }
}

const BUSINESS_TYPES = [
  'Optical Wholesaler', 'Regional Distributor', 'Optical Chain', 'Eyewear Retailer',
  'Importer / Exporter', 'Established B2B Business', 'Market-Focused Entrepreneur', 'Other',
];

const EMPTY_FORM = {
  name: '', company_name: '', country: '', state_region_city: '', email: '', phone: '',
  years_in_business: '', business_type: '', existing_network: '', num_retailers: '',
  territory: '', brands_distributed: '', purchase_potential: '', message: '',
};

export default function BecomeDistributor() {
  const { content: c } = useSite();
  const [form, setForm] = useState(EMPTY_FORM);
  const [captchaToken, setCaptchaToken] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useSEO({
    title: c['page.become-distributor.meta_title'] || 'Become a Distributor',
    description: c['page.become-distributor.meta_description'],
    canonical: `${window.location.origin}/store/page/become-distributor`,
  });

  const whyPartnerItems = parseJson(c['page.become-distributor.why_partner_items'], []);
  const lookingItems = parseJson(c['page.become-distributor.looking_items'], []);
  const benefitsItems = parseJson(c['page.become-distributor.benefits_items'], []);
  const heroImg = c['page.become-distributor.hero_image'];
  const partnerImg = c['page.become-distributor.partner_image'];

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async e => {
    e.preventDefault(); setErr('');
    if (!form.name || !form.company_name || !form.email || !form.message) {
      setErr('Please fill in your name, company name, email and a note about your business.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return setErr('Please enter a valid email address');
    if (!form.phone || !form.phone.trim()) return setErr('Please enter your phone number');
    setBusy(true);
    try {
      await axios.post('/api/become-distributor/enquiry', { ...form, captcha_token: captchaToken });
      setSent(true);
      trackEvent('distributor_application', { country: form.country, business_type: form.business_type });
    } catch (e2) { setErr(e2.response?.data?.error || 'Could not submit. Please try again.'); }
    setBusy(false);
  };

  return (
    <div style={{ background: 'var(--sf-body-bg)' }}>
      {/* HERO */}
      <section style={{
        position: 'relative', overflow: 'hidden',
        background: heroImg
          ? `linear-gradient(160deg, rgba(10,20,30,0.82), rgba(10,20,30,0.55)), url(${IMG(heroImg)}) center/cover`
          : 'linear-gradient(155deg, var(--sf-primary, #191919) 0%, #0e2a3d 55%, var(--sf-primary-dark, #000) 100%)',
        color: '#fff',
      }}>
        <NetworkPattern />
        <div style={{ position: 'relative', zIndex: 1, maxWidth: 820, margin: '0 auto', padding: '84px 24px 76px', textAlign: 'center' }}>
          {c['page.become-distributor.hero_badge'] && (
            <span style={{
              display: 'inline-block', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)',
              color: 'var(--sf-accent)', fontSize: 11.5, fontWeight: 700, letterSpacing: 1.6, textTransform: 'uppercase',
              padding: '6px 18px', borderRadius: 20, marginBottom: 22,
            }}>
              {c['page.become-distributor.hero_badge']}
            </span>
          )}
          <h1 style={{ fontSize: 'clamp(30px,4.6vw,46px)', fontWeight: 800, lineHeight: 1.18, margin: '0 0 14px' }}>
            {c['page.become-distributor.hero_heading'] || 'Become Our Distributor'}
          </h1>
          <p style={{ fontSize: 17, fontWeight: 500, color: 'var(--sf-accent)', margin: '0 0 20px' }}>
            {c['page.become-distributor.hero_subheading']}
          </p>
          <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.82)', maxWidth: 640, margin: '0 auto', lineHeight: 1.75 }}>
            {c['page.become-distributor.hero_intro']}
          </p>
          <a href="#distributor-form" className="sf-btn-outline" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, textDecoration: 'none', marginTop: 30,
            border: '1.5px solid var(--sf-accent)', color: '#fff', padding: '13px 30px', borderRadius: 28,
            fontWeight: 700, fontSize: 14.5,
          }}>
            <i className="ti ti-arrow-down" /> {c['page.become-distributor.hero_cta_text'] || 'Apply to Become a Distributor'}
          </a>
        </div>
      </section>

      {/* MORE THAN A SUPPLIER */}
      <section style={{ maxWidth: 1160, margin: '0 auto', padding: '64px 24px 10px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: partnerImg ? 'minmax(0,1fr) 340px' : '1fr', gap: 44, alignItems: 'center' }} className="bd-two-col">
          <div>
            <h2 style={{ fontSize: 26, fontWeight: 800, color: 'var(--sf-primary)', margin: '0 0 16px' }}>
              {c['page.become-distributor.partner_heading']}
            </h2>
            <p style={{ fontSize: 14.5, color: 'var(--sf-muted)', lineHeight: 1.8, margin: '0 0 14px' }}>{c['page.become-distributor.partner_para1']}</p>
            <p style={{ fontSize: 14.5, color: 'var(--sf-muted)', lineHeight: 1.8, margin: '0 0 18px' }}>{c['page.become-distributor.partner_para2']}</p>
            {c['page.become-distributor.partner_tagline'] && (
              <p style={{ fontSize: 15.5, fontWeight: 700, color: 'var(--sf-primary)', margin: 0, borderLeft: '3px solid var(--sf-accent)', paddingLeft: 14 }}>
                {c['page.become-distributor.partner_tagline']}
              </p>
            )}
          </div>
          {partnerImg && (
            <img src={IMG(partnerImg)} alt="" style={{ width: '100%', borderRadius: 16, display: 'block', boxShadow: '0 14px 40px rgba(0,0,0,0.12)' }} />
          )}
        </div>
      </section>

      {/* WHY PARTNER — 7 REASON CARDS */}
      {whyPartnerItems.length > 0 && (
        <section style={{ maxWidth: 1200, margin: '64px auto 0', padding: '0 24px' }}>
          <h2 style={{ fontSize: 26, fontWeight: 800, color: 'var(--sf-primary)', textAlign: 'center', margin: '0 0 36px' }}>
            {c['page.become-distributor.why_partner_heading'] || 'Why Partner With Prakaash Eyewear?'}
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 20 }}>
            {whyPartnerItems.map((g, i) => (
              <div key={i} className="bd-card" style={{ background: '#fff', border: '1px solid #eee', borderRadius: 14, padding: '28px 22px' }}>
                <div style={{
                  width: 50, height: 50, borderRadius: 12, background: 'color-mix(in srgb, var(--sf-accent) 20%, #fff)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16,
                }}>
                  <i className={`ti ti-${g.icon}`} style={{ fontSize: 23, color: 'var(--sf-primary)' }} />
                </div>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--sf-primary)', margin: '0 0 8px' }}>{g.title}</h3>
                <p style={{ fontSize: 12.5, color: 'var(--sf-muted)', lineHeight: 1.7, margin: 0 }}>{g.text}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* WHO ARE WE LOOKING FOR */}
      <section style={{ maxWidth: 1160, margin: '68px auto 0', padding: '0 24px' }}>
        <div style={{ background: 'var(--sf-primary)', borderRadius: 20, padding: '46px 40px', color: '#fff', display: 'grid', gridTemplateColumns: 'minmax(0,1.2fr) minmax(220px,1fr)', gap: 36 }} className="bd-two-col">
          <div>
            <h2 style={{ fontSize: 24, fontWeight: 800, margin: '0 0 12px' }}>{c['page.become-distributor.looking_heading'] || 'Who Are We Looking For?'}</h2>
            <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.78)', lineHeight: 1.75, margin: '0 0 10px', maxWidth: 480 }}>
              {c['page.become-distributor.looking_intro']}
            </p>
            {c['page.become-distributor.looking_footnote'] && (
              <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', lineHeight: 1.65, margin: 0, maxWidth: 480 }}>
                {c['page.become-distributor.looking_footnote']}
              </p>
            )}
          </div>
          <div>
            {lookingItems.map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', fontSize: 13.5, fontWeight: 500 }}>
                <i className="ti ti-circle-check-filled" style={{ fontSize: 17, color: 'var(--sf-accent)', flexShrink: 0 }} />
                <span>{item.title}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY BECOME A DISTRIBUTOR — 5 BENEFIT CARDS */}
      {benefitsItems.length > 0 && (
        <section style={{ maxWidth: 1200, margin: '68px auto 0', padding: '0 24px' }}>
          <h2 style={{ fontSize: 26, fontWeight: 800, color: 'var(--sf-primary)', textAlign: 'center', margin: '0 0 6px' }}>
            {c['page.become-distributor.benefits_heading'] || 'Why Become a Distributor?'}
          </h2>
          <p style={{ fontSize: 14, color: 'var(--sf-muted)', textAlign: 'center', maxWidth: 620, margin: '0 auto 36px', lineHeight: 1.7 }}>
            {c['page.become-distributor.benefits_intro']}
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 18 }}>
            {benefitsItems.map((b, i) => (
              <div key={i} className="bd-card" style={{ textAlign: 'center', padding: '24px 16px', background: '#fff', border: '1px solid #eee', borderRadius: 14 }}>
                <div style={{
                  width: 52, height: 52, borderRadius: '50%', background: 'color-mix(in srgb, var(--sf-accent) 22%, #fff)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px',
                }}>
                  <i className={`ti ti-${b.icon}`} style={{ fontSize: 22, color: 'var(--sf-primary)' }} />
                </div>
                <h3 style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--sf-primary)', margin: '0 0 6px' }}>{b.title}</h3>
                <p style={{ fontSize: 12, color: 'var(--sf-muted)', lineHeight: 1.6, margin: 0 }}>{b.text}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* TERRITORY */}
      {c['page.become-distributor.territory_body'] && (
        <section style={{ maxWidth: 800, margin: '68px auto 0', padding: '0 24px', textAlign: 'center' }}>
          <i className="ti ti-map-2" style={{ fontSize: 34, color: 'var(--sf-accent)', marginBottom: 14, display: 'block' }} />
          <h2 style={{ fontSize: 23, fontWeight: 800, color: 'var(--sf-primary)', margin: '0 0 14px' }}>
            {c['page.become-distributor.territory_heading'] || 'Your Territory. Your Network. Our Eyewear.'}
          </h2>
          <p style={{ fontSize: 14.5, color: 'var(--sf-muted)', lineHeight: 1.85, margin: 0 }}>
            {c['page.become-distributor.territory_body']}
          </p>
        </section>
      )}

      {/* MID-PAGE CTA BANNER */}
      {c['page.become-distributor.cta_heading'] && (
        <section style={{ maxWidth: 1160, margin: '68px auto 0', padding: '0 24px' }}>
          <div style={{
            background: 'linear-gradient(115deg, var(--sf-primary) 0%, #0e2a3d 100%)', borderRadius: 20,
            padding: '40px 40px', color: '#fff', display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div style={{ flex: '1 1 380px' }}>
              <h3 style={{ fontSize: 21, fontWeight: 800, margin: '0 0 8px' }}>{c['page.become-distributor.cta_heading']}</h3>
              <p style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.78)', lineHeight: 1.7, margin: 0, maxWidth: 520 }}>
                {c['page.become-distributor.cta_body']}
              </p>
            </div>
            <a href="#distributor-form" className="sf-btn-outline" style={{
              flexShrink: 0, textDecoration: 'none', border: '1.5px solid var(--sf-accent)', color: '#fff',
              padding: '13px 28px', borderRadius: 26, fontWeight: 700, fontSize: 14,
            }}>
              {c['page.become-distributor.cta_button_text'] || 'Apply to Become a Distributor'}
            </a>
          </div>
        </section>
      )}

      {/* ENQUIRY FORM */}
      <section id="distributor-form" style={{ maxWidth: 780, margin: '68px auto 0', padding: '0 24px' }}>
        <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 20, padding: '38px 34px', boxShadow: '0 20px 50px rgba(0,0,0,0.06)' }}>
          {sent ? (
            <div style={{ textAlign: 'center', padding: '30px 10px' }}>
              <i className="ti ti-circle-check" style={{ fontSize: 48, color: '#16a34a' }} />
              <h3 style={{ marginTop: 12, fontSize: 20 }}>Thank you!</h3>
              <p style={{ color: 'var(--sf-muted)', fontSize: 14.5, maxWidth: 420, margin: '8px auto 0', lineHeight: 1.7 }}>
                Our distribution team will review your application and contact you regarding the next steps.
              </p>
            </div>
          ) : (
            <form onSubmit={submit}>
              <div style={{ textAlign: 'center', marginBottom: 26 }}>
                <span style={{
                  display: 'inline-block', background: 'color-mix(in srgb, var(--sf-accent) 18%, #fff)', color: 'var(--sf-primary)',
                  fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '5px 14px', borderRadius: 16, marginBottom: 12,
                }}>
                  {c['page.become-distributor.form_heading'] || 'Distributor Enquiry Form'}
                </span>
                <p style={{ fontSize: 13.5, color: 'var(--sf-muted)', lineHeight: 1.7, margin: 0, maxWidth: 480, marginLeft: 'auto', marginRight: 'auto' }}>
                  {c['page.become-distributor.form_intro']}
                </p>
              </div>

              {err && <div style={{ background: '#fef2f2', color: '#b91c1c', fontSize: 13, padding: '10px 12px', borderRadius: 8, marginBottom: 16 }}>{err}</div>}

              <FieldRow>
                <Field label="Full Name *" value={form.name} onChange={set('name')} required />
                <Field label="Company / Business Name *" value={form.company_name} onChange={set('company_name')} required />
              </FieldRow>
              <FieldRow>
                <Field label="Country *" value={form.country} onChange={set('country')} required />
                <Field label="State / Region / City *" value={form.state_region_city} onChange={set('state_region_city')} required />
              </FieldRow>
              <FieldRow>
                <Field label="Email Address *" type="email" value={form.email} onChange={set('email')} required />
                <div style={{ flex: 1 }}>
                  <label style={lbl}>Phone / WhatsApp Number *</label>
                  <StorePhoneInput value={form.phone} onChange={v => setForm(f => ({ ...f, phone: v }))} required />
                </div>
              </FieldRow>
              <FieldRow>
                <Field label="Years in Business" value={form.years_in_business} onChange={set('years_in_business')} />
                <div style={{ flex: 1 }}>
                  <label style={lbl}>Current Business Type *</label>
                  <select className="bd-select" value={form.business_type} onChange={set('business_type')} required>
                    <option value="">Select…</option>
                    {BUSINESS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </FieldRow>
              <FieldRow>
                <Field label="Existing Optical / Eyewear Network" value={form.existing_network} onChange={set('existing_network')} />
                <Field label="Number of Retailers / Customers Served" value={form.num_retailers} onChange={set('num_retailers')} />
              </FieldRow>
              <FieldRow>
                <Field label="Territory You Want to Develop *" value={form.territory} onChange={set('territory')} required />
                <Field label="Brands Currently Distributed" value={form.brands_distributed} onChange={set('brands_distributed')} />
              </FieldRow>
              <div style={{ marginBottom: 16 }}>
                <label style={lbl}>Estimated Monthly / Annual Purchase Potential</label>
                <input className="bd-input" value={form.purchase_potential} onChange={set('purchase_potential')} placeholder="e.g. $5,000 / month, or 500 units / year" />
              </div>
              <div style={{ marginBottom: 18 }}>
                <label style={lbl}>Tell Us About Your Business *</label>
                <textarea className="bd-input" value={form.message} onChange={set('message')} rows={4} required
                  placeholder="Your market, existing network, customer base, business experience and growth plans…" />
              </div>

              <Captcha onToken={setCaptchaToken} />
              <button type="submit" disabled={busy} className="sf-btn-outline" style={{
                width: '100%', padding: 13, border: '1.5px solid var(--sf-accent)', borderRadius: 10,
                fontSize: 14.5, fontWeight: 700, cursor: 'pointer', marginTop: 6,
              }}>
                {busy ? 'Submitting…' : (c['page.become-distributor.form_submit_text'] || 'Submit Distributor Application')}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* CLOSING */}
      <section style={{ maxWidth: 900, margin: '70px auto 0', padding: '0 24px 76px', textAlign: 'center' }}>
        {c['page.become-distributor.closing_line'] && (
          <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--sf-primary)', margin: '0 0 10px' }}>
            {c['page.become-distributor.closing_line']}
          </h2>
        )}
        <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--sf-primary)', marginTop: 18 }}>Prakaash Eyewear Pvt. Ltd.</div>
        {c['page.become-distributor.closing_tagline'] && (
          <div style={{ fontSize: 12.5, color: 'var(--sf-muted)', marginTop: 4 }}>{c['page.become-distributor.closing_tagline']}</div>
        )}
      </section>

      <style>{`
        @media (max-width: 860px) {
          .bd-two-col { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 560px) {
          .bd-field-row { flex-direction: column !important; }
        }
        .bd-card { transition: transform .18s ease, box-shadow .18s ease; }
        .bd-card:hover { transform: translateY(-3px); box-shadow: 0 12px 30px rgba(0,0,0,0.08); }
        .bd-input, .bd-select {
          width: 100%; padding: 10px 12px; border: 1.5px solid #e5e5e5; border-radius: 8px; font-size: 14px;
          box-sizing: border-box; font-family: inherit; background: #fff;
        }
        .bd-input:focus, .bd-select:focus { outline: none; border-color: var(--sf-accent); }
        textarea.bd-input { resize: vertical; }
      `}</style>
    </div>
  );
}

function FieldRow({ children }) {
  return <div className="bd-field-row" style={{ display: 'flex', gap: 14, marginBottom: 16 }}>{children}</div>;
}

function Field({ label, value, onChange, type = 'text', required }) {
  return (
    <div style={{ flex: 1 }}>
      <label style={lbl}>{label}</label>
      <input className="bd-input" type={type} value={value} onChange={onChange} required={required} />
    </div>
  );
}

const lbl = { fontSize: 12.5, fontWeight: 500, color: '#555', display: 'block', marginBottom: 5 };

// Subtle decorative "global network" pattern — dots + connecting lines in the
// site's own accent colour, evoking a distribution network without needing a
// stock photo. Purely CSS-var driven so it re-themes with the brand palette.
function NetworkPattern() {
  const dots = [
    [60, 60], [180, 110], [320, 50], [460, 130], [600, 70], [740, 140], [860, 60],
    [120, 200], [280, 230], [420, 190], [560, 250], [700, 210], [820, 240],
  ];
  const lines = [[0, 1], [1, 2], [1, 8], [2, 3], [3, 4], [4, 5], [4, 10], [5, 6], [5, 12], [7, 8], [8, 9], [9, 10], [10, 11], [11, 12]];
  return (
    <svg viewBox="0 0 900 300" preserveAspectRatio="xMidYMid slice" style={{
      position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.16, pointerEvents: 'none',
    }}>
      {lines.map(([a, b], i) => (
        <line key={i} x1={dots[a][0]} y1={dots[a][1]} x2={dots[b][0]} y2={dots[b][1]} stroke="var(--sf-accent, #85cde9)" strokeWidth="1" />
      ))}
      {dots.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={i % 3 === 0 ? 4 : 2.5} fill="var(--sf-accent, #85cde9)" />
      ))}
    </svg>
  );
}
